import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { PinyinKeyboard } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard";
import { LearnFlashcardHorizontalGestureBlocker } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import {
  splitToneNumbersPinyinSyllables,
  toneNumberSyllableForComparison,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { View } from "@-/com.npmjs.react-native";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { useRevealMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import { useCallback, useMemo } from "react";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { DrillPageSentenceDrill } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.sentence-drill";

/**
 * The character-mode footer: type the current word's reading, one syllable at a time.
 *
 * `PinyinKeyboard`'s contract is the thing to get right here. `onPinyinSelect` is a PREVIEW SETTER,
 * not a per-syllable event — it fires for every keystroke, tone key, and clear, always with the
 * whole in-progress value. A syllable is only "finished" when `onCheckPassed` fires, which the
 * keyboard decides by matching the preview against `correctPinyinList`.
 *
 * So this view feeds it ONE expected syllable at a time (`correctPinyinList` below), collects each
 * confirmed syllable, and submits an attempt once it has as many as the target word's reading has.
 * Multi-syllable sequencing lives here rather than in the keyboard because every other caller wants
 * the keyboard to stay single-syllable.
 *
 * Submitting a READING rather than characters is deliberate: the matcher accepts a token by pinyin
 * equivalence, so typing the right reading is a correct answer even though the user never picked
 * the characters. That is what "fill the sentence in by typing" means here.
 */
export function DrillFooterSentenceGlyphsView({
  sentenceDrill,
}: {
  sentenceDrill: DrillPageSentenceDrill;
}) {
  const { pinyinKeyboardLayout } = useActorUser();
  // Reveal goes through the shared mutation, which branches to the in-place sentence reveal. The
  // background tap target is disabled for these kinds, so this button is the ONLY way to end the
  // drill and must not bypass that chokepoint.
  const revealMutation = useRevealMutation();
  const [pinyinPreview, setPinyinPreview] = useRouteBodyState("sentence-pinyin-preview", "");
  const [confirmedText, setConfirmedText] = useRouteBodyState("sentence-confirmed-syllables", "");
  const confirmedSyllables = useMemo(
    () => (confirmedText === "" ? [] : confirmedText.split(" ")),
    [confirmedText],
  );
  const setConfirmedSyllables = useCallback(
    (syllables: readonly string[]) => {
      setConfirmedText(syllables.join(" "));
    },
    [setConfirmedText],
  );

  const { dispatch, state } = sentenceDrill;
  const cursorPinyin = state.tokens[state.cursorIndex]?.pinyin ?? null;

  // The target word's reading, split into the syllables the user must produce in order.
  const expectedSyllables = useMemo(
    () => (cursorPinyin === null ? [] : splitToneNumbersPinyinSyllables(cursorPinyin)),
    [cursorPinyin],
  );

  // Only ever the NEXT syllable: the keyboard checks one syllable, so handing it the whole word's
  // reading would never match and `onCheckPassed` would never fire.
  const correctPinyinList = useMemo(() => {
    const next = expectedSyllables[arrayLength(confirmedSyllables)];
    return next === undefined ? [] : [next];
  }, [confirmedSyllables, expectedSyllables]);

  const resetInput = useCallback(() => {
    setPinyinPreview("");
    setConfirmedSyllables([]);
  }, [setConfirmedSyllables, setPinyinPreview]);

  const handleCheckPassed = useCallback(
    (value: string) => {
      const next = [...confirmedSyllables, toneNumberSyllableForComparison(value)];
      setPinyinPreview("");
      if (arrayLength(next) < arrayLength(expectedSyllables)) {
        setConfirmedSyllables(next);
        return;
      }
      // Every syllable of the word is in — submit it as one produced token.
      setConfirmedSyllables([]);
      dispatch({ kind: "attempt", produced: [{ pinyin: next.join(" "), word: "" }] });
    },
    [confirmedSyllables, dispatch, expectedSyllables, setConfirmedSyllables, setPinyinPreview],
  );

  // Dispatched as `wrong-answer`, NOT as a non-matching `attempt`. An attempt that aligns to
  // nothing is deliberately inert (in speech mode it is usually the mic mishearing, and escalating
  // there would spend a hint on noise) — so routing a typed mistake through `attempt` would make it
  // do nothing at all: no feedback, no hint, the blank just sits there.
  const handleCheckFailed = useCallback(() => {
    setPinyinPreview("");
    setConfirmedSyllables([]);
    dispatch({ kind: "wrong-answer" });
  }, [dispatch, setConfirmedSyllables, setPinyinPreview]);

  const handleHint = useCallback(() => {
    resetInput();
    dispatch({ kind: "request-hint" });
  }, [dispatch, resetInput]);

  return (
    <View className="flex w-full flex-col">
      <View className="flex-row items-center justify-between px-4 pb-2">
        <Text className="text-sm leading-tight" colorVariant="muted" testID="sentence-drill-typed">
          {[...confirmedSyllables, pinyinPreview].filter((part) => part.length > 0).join(" ")}
        </Text>
        <View className="flex-row items-center gap-2">
          <Button
            accessibilityLabel="Show a hint for this word"
            onPress={handleHint}
            size="sm"
            testID="sentence-drill-hint"
            variant="ghost"
          >
            <Text>Hint</Text>
          </Button>
          <Button
            accessibilityLabel="Reveal this word and finish"
            onPress={() => {
              revealMutation.mutate();
            }}
            size="sm"
            testID="sentence-drill-reveal"
            variant="outline"
          >
            <Text>Reveal</Text>
          </Button>
        </View>
      </View>
      {/* Keys and the gaps around them type; a drifting touch there must not drag the card. */}
      <LearnFlashcardHorizontalGestureBlocker>
        <PinyinKeyboard
          backspaceBehavior={
            pinyinKeyboardLayout === "qwerty" ? "deleteLastCharacter" : "clearInput"
          }
          correctPinyinList={correctPinyinList}
          layout={pinyinKeyboardLayout === "qwerty" ? "qwerty" : "shuangpin"}
          onCheckFailed={handleCheckFailed}
          onCheckPassed={handleCheckPassed}
          onPinyinSelect={setPinyinPreview}
          pinyinPreview={pinyinPreview}
        />
      </LearnFlashcardHorizontalGestureBlocker>
    </View>
  );
}
